/**
 * URL safety checks for link-preview extraction (SSRF guard).
 *
 * Every address the extractor is about to fetch — the one the user typed, and
 * again the target of every redirect it follows — goes through
 * `validateFetchableUrl` first. The rules are deliberately conservative: a
 * preview is a nice-to-have, so refusing a borderline address costs nothing
 * while allowing one can turn the extractor into a proxy for the internal
 * network.
 *
 * The module is free of React Native imports on purpose: it is shared verbatim
 * by the app and by the Cloudflare Worker in `workers/link-preview`, which is
 * where the actual fetching happens.
 */

export type UrlSafetyFailure =
  | "invalid-url"
  | "unsupported-scheme"
  | "embedded-credentials"
  | "blocked-host"
  | "private-address"
  | "blocked-port";

export type UrlSafetyResult =
  | { safe: true; url: URL }
  | { safe: false; reason: UrlSafetyFailure };

/** Only the web schemes. `file:`, `ftp:`, `data:`, `gopher:` etc. are out. */
const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

/**
 * Ports are restricted to the web defaults. Public hosts are the only ones that
 * get this far anyway, but pinning the port closes off the "public DNS name
 * pointing at an odd service port" shape.
 */
const ALLOWED_PORTS = new Set(["", "80", "443"]);

/** Names that resolve to the machine itself or to a cloud metadata service. */
const BLOCKED_HOSTNAMES = new Set([
  "localhost",
  "ip6-localhost",
  "ip6-loopback",
  "metadata",
  "metadata.google.internal",
  "metadata.goog",
  "instance-data",
  // The numeric metadata endpoints (169.254.169.254, 100.100.100.200) are
  // covered by the link-local and CGNAT ranges below.
]);

/** Suffixes reserved for private/internal naming. */
const BLOCKED_HOST_SUFFIXES = [
  ".localhost",
  ".local",
  ".internal",
  ".intranet",
  ".lan",
  ".corp",
  ".home",
  ".home.arpa",
  ".onion",
  ".test",
  ".invalid",
  ".example",
];

/**
 * Parse an IPv4 literal in any of the forms `URL` leaves intact — dotted quad,
 * but also the octal (`0177.0.0.1`), hex (`0x7f.0.0.1`) and integer
 * (`2130706433`) spellings that all still resolve to 127.0.0.1.
 *
 * Returns the address as a 32-bit number, or null when `host` is not an IPv4
 * literal at all (i.e. it is a DNS name).
 */
export const parseIpv4 = (host: string): number | null => {
  const parts = host.split(".");
  if (parts.length > 4) return null;

  const values: number[] = [];
  for (const part of parts) {
    if (part === "") return null;
    let value: number;
    if (/^0[xX][0-9a-fA-F]+$/.test(part)) value = parseInt(part.slice(2), 16);
    else if (/^0[0-7]+$/.test(part)) value = parseInt(part.slice(1), 8);
    else if (/^\d+$/.test(part)) value = parseInt(part, 10);
    else return null;
    if (!Number.isFinite(value) || value < 0) return null;
    values.push(value);
  }

  // The trailing part absorbs the remaining octets: "127.1" is 127.0.0.1.
  const last = values[values.length - 1];
  const maxLast = 2 ** (8 * (4 - values.length + 1));
  if (last >= maxLast) return null;
  if (values.slice(0, -1).some((value) => value > 255)) return null;

  let address = last;
  values.slice(0, -1).forEach((value, index) => {
    address += value * 2 ** (8 * (3 - index));
  });
  return address >>> 0;
};

/** CIDR blocks that must never be fetched, as [network, prefix length]. */
const BLOCKED_IPV4_RANGES: readonly (readonly [string, number])[] = [
  ["0.0.0.0", 8], // "this network"
  ["10.0.0.0", 8], // RFC1918
  ["100.64.0.0", 10], // carrier-grade NAT
  ["127.0.0.0", 8], // loopback
  ["169.254.0.0", 16], // link-local, incl. the 169.254.169.254 metadata endpoint
  ["172.16.0.0", 12], // RFC1918
  ["192.0.0.0", 24], // IETF protocol assignments
  ["192.0.2.0", 24], // TEST-NET-1
  ["192.88.99.0", 24], // 6to4 relay anycast
  ["192.168.0.0", 16], // RFC1918
  ["198.18.0.0", 15], // benchmarking
  ["198.51.100.0", 24], // TEST-NET-2
  ["203.0.113.0", 24], // TEST-NET-3
  ["224.0.0.0", 4], // multicast
  ["240.0.0.0", 4], // reserved, incl. 255.255.255.255
];

const ipv4ToNumber = (dotted: string): number =>
  dotted
    .split(".")
    .reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0;

export const isPrivateIpv4 = (address: number): boolean =>
  BLOCKED_IPV4_RANGES.some(([network, prefix]) => {
    const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
    return (address & mask) >>> 0 === (ipv4ToNumber(network) & mask) >>> 0;
  });

/**
 * IPv6 literals are refused wholesale rather than range-checked. Public sites
 * are reached by name, so the only realistic reason to hand the extractor a
 * bare v6 address is to slip past the v4 range list — `[::1]`, `[::ffff:127.0.0.1]`
 * and `[fd00::1]` all point somewhere we will not go.
 */
const isIpv6Literal = (hostname: string): boolean => hostname.startsWith("[");

export const validateFetchableUrl = (raw: string): UrlSafetyResult => {
  if (typeof raw !== "string" || raw.trim() === "") {
    return { safe: false, reason: "invalid-url" };
  }

  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return { safe: false, reason: "invalid-url" };
  }

  if (!ALLOWED_PROTOCOLS.has(url.protocol)) {
    return { safe: false, reason: "unsupported-scheme" };
  }

  // `https://user:pass@internal/` is a classic way to confuse naive parsers.
  if (url.username !== "" || url.password !== "") {
    return { safe: false, reason: "embedded-credentials" };
  }

  if (!ALLOWED_PORTS.has(url.port)) {
    return { safe: false, reason: "blocked-port" };
  }

  // A trailing dot is a legal FQDN spelling ("localhost.") that would otherwise
  // slip past the name list.
  const hostname = url.hostname.toLowerCase().replace(/\.$/, "");
  if (hostname === "") return { safe: false, reason: "invalid-url" };

  if (isIpv6Literal(url.hostname)) {
    return { safe: false, reason: "private-address" };
  }

  if (BLOCKED_HOSTNAMES.has(hostname)) {
    return { safe: false, reason: "blocked-host" };
  }

  if (BLOCKED_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix))) {
    return { safe: false, reason: "blocked-host" };
  }

  const ipv4 = parseIpv4(hostname);
  if (ipv4 !== null) {
    if (isPrivateIpv4(ipv4)) return { safe: false, reason: "private-address" };
    return { safe: true, url };
  }

  // A bare, dotless name ("intranet", "router") is an internal host by
  // definition — public DNS always has at least one dot.
  if (!hostname.includes(".")) {
    return { safe: false, reason: "blocked-host" };
  }

  return { safe: true, url };
};

export const isSafeFetchableUrl = (raw: string): boolean =>
  validateFetchableUrl(raw).safe;

/**
 * Images are loaded by the client rather than the extractor, so they get the
 * same host rules but are allowed any port — a CDN on :8443 is common and the
 * request leaves the user's device, not the server.
 */
export const isSafeImageUrl = (raw: string): boolean => {
  const result = validateFetchableUrl(raw);
  if (result.safe) return true;
  if (result.reason !== "blocked-port") return false;
  try {
    const url = new URL(raw.trim());
    return validateFetchableUrl(`${url.protocol}//${url.host.split(":")[0]}${url.pathname}`)
      .safe;
  } catch {
    return false;
  }
};
